import { CurrencyCurrency } from '@linktivity/ars-neo';

/** Currencies with no minor unit, so amounts are whole numbers. */
const ZERO_DECIMAL_CURRENCIES: string[] = [
  CurrencyCurrency.Jpy,
  CurrencyCurrency.Krw
];

/**
 * Digits after the decimal point for a currency. The single source of truth
 * for money precision: both the rendered price and the amounts reported to
 * analytics round through it, so the two can never disagree.
 */
export function getCurrencyDecimals(currency?: string): number {
  return currency && ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2;
}

/** Round to the currency's own precision. */
export function roundToCurrency(amount: number, currency?: string): number {
  const factor = 10 ** getCurrencyDecimals(currency);
  return Math.round(amount * factor) / factor;
}

interface DisplayPriceSource {
  displayAmount?: number;
  displayCurrency?: CurrencyCurrency;
}

/**
 * Resolve the currency code that matches the displayed amount source.
 *
 * When showing other currencies the display (converted) currency is used;
 * otherwise the base booking currency is used. Each falls back to the other
 * so older bookings that only have one of the two still render something.
 */
export function resolveCurrencyCode(
  currency: CurrencyCurrency | undefined,
  displayCurrency: CurrencyCurrency | undefined,
  showOtherCurrencies: boolean
): CurrencyCurrency | undefined {
  return showOtherCurrencies ? (displayCurrency ?? currency) : currency;
}

/**
 * Resolve the price info to display based on `showOtherCurrencies`.
 *
 * When `showOtherCurrencies` is true the display (converted) values are
 * preferred; otherwise the base values are used.
 */
export function resolveProductPriceInfo(
  source: DisplayPriceSource,
  baseAmount: number,
  fallbackCurrency: CurrencyCurrency,
  showOtherCurrencies: boolean
): { amount: number; currencyCode: CurrencyCurrency } {
  if (showOtherCurrencies && source.displayAmount != null) {
    return {
      amount: source.displayAmount,
      currencyCode: source.displayCurrency ?? fallbackCurrency
    };
  }
  return {
    amount: baseAmount,
    currencyCode: fallbackCurrency
  };
}

interface PaidAmountSource {
  amount?: number | null;
  displayAmount?: number | null;
  displayFinalAmount?: number | null;
  finalAmount?: number | null;
}

/**
 * Resolve the paid amount for a booking based on discount and currency display settings.
 *
 * When a discount is applied, `displayFinalAmount` (post-discount display value) is preferred
 * over `displayAmount` for the other-currencies path.
 */
export function getPaidAmount(
  { displayAmount, displayFinalAmount, finalAmount, amount }: PaidAmountSource,
  showOtherCurrencies: boolean
): number {
  if (showOtherCurrencies) {
    return displayFinalAmount ?? displayAmount ?? finalAmount ?? amount ?? 0;
  }
  return finalAmount ?? amount ?? 0;
}

/**
 * Resolve the unit-level amount based on `showOtherCurrencies`.
 *
 * When `showOtherCurrencies` is true, prefer `displayAmount`;
 * otherwise prefer `amount`. If the preferred value is missing,
 * fall back to the other amount before returning `0`.
 */
export function resolveUnitAmount(
  unit: { displayAmount?: number; amount?: number } | null | undefined,
  showOtherCurrencies: boolean
): number {
  if (showOtherCurrencies) {
    return unit?.displayAmount ?? unit?.amount ?? 0;
  }
  return unit?.amount ?? unit?.displayAmount ?? 0;
}
